import React from "react";
import { usePrevious } from "react-use";
import { type FragmentOf, graphql, readFragment } from "@/graphql";

export type ServerPaginationProperties = ReturnType<typeof usePagination>;
export type ResetPagination = ServerPaginationProperties["controls"]["handleResetPagination"];

export type PageSize = "10" | "20" | "50" | "100";

export const PAGE_SIZE_OPTIONS = [
  { value: "10", label: "10" },
  { value: "20", label: "20" },
  { value: "50", label: "50" },
  { value: "100", label: "100" },
] satisfies Array<{ value: PageSize; label: PageSize }>;

export const INITIAL_PAGE_SIZE = "10" satisfies PageSize;

type PaginationState = {
  pageSize: PageSize;
  currentPage: number;
  paginationQueryVariables: {
    last: Maybe<number>;
    before: Maybe<string>;
    first: Maybe<number>;
    after: Maybe<string>;
  };
};

type Action =
  | { type: "GO_TO_FIRST_PAGE" }
  | { type: "GO_TO_NEXT_PAGE"; endCursor: Maybe<string> }
  | {
      type: "GO_TO_PREVIOUS_PAGE";
      startCursor: Maybe<string>;
    }
  | { type: "GO_TO_LAST_PAGE"; totalCount: number }
  | { type: "SET_PAGE_SIZE"; pageSize: PageSize }
  | { type: "RESET_PAGINATION" };

const paginationReducer = (state: PaginationState, action: Action): PaginationState => {
  switch (action.type) {
    case "GO_TO_FIRST_PAGE": {
      return {
        ...state,
        currentPage: 1,
        paginationQueryVariables: {
          after: undefined,
          before: undefined,
          last: undefined,
          first: Number(state.pageSize),
        },
      };
    }

    case "GO_TO_LAST_PAGE": {
      return {
        ...state,
        currentPage: Math.ceil(action.totalCount / Number(state.pageSize)),
        paginationQueryVariables: {
          first: undefined,
          after: undefined,
          last: action.totalCount % Number(state.pageSize) || Number(state.pageSize),
          before: undefined,
        },
      };
    }

    case "GO_TO_NEXT_PAGE": {
      return {
        ...state,
        currentPage: state.currentPage + 1,
        paginationQueryVariables: {
          first: Number(state.pageSize),
          after: action.endCursor,
          last: undefined,
          before: undefined,
        },
      };
    }

    case "GO_TO_PREVIOUS_PAGE": {
      return {
        ...state,
        currentPage: state.currentPage - 1,
        paginationQueryVariables: {
          first: undefined,
          after: undefined,
          last: Number(state.pageSize),
          before: action.startCursor,
        },
      };
    }

    case "SET_PAGE_SIZE": {
      return {
        ...state,
        pageSize: action.pageSize,
        currentPage: 1,
        paginationQueryVariables: {
          first: Number(action.pageSize),
          last: undefined,
          before: undefined,
          after: undefined,
        },
      };
    }

    case "RESET_PAGINATION": {
      return {
        ...state,
        currentPage: 1,
        paginationQueryVariables: {
          first: Number(state.pageSize),
          last: undefined,
          before: undefined,
          after: undefined,
        },
      };
    }

    default: {
      const _exhaustiveCheck: never = action;
      return _exhaustiveCheck;
    }
  }
};

export const pageInfoFragment = graphql(`
  fragment PageInfo on PageInfo {
    startCursor
    endCursor
  }
`);

export const usePagination = ({
  totalCount,
  pageInfo,
  loading,
  urlPageSize,
  localStoragePageSize,
  onUrlPageSizeChange,
  onPageSizePreferenceChange,
}: {
  totalCount: number;
  pageInfo: FragmentOf<typeof pageInfoFragment> | undefined;
  loading: boolean;
  urlPageSize?: PageSize;
  localStoragePageSize?: PageSize;
  onUrlPageSizeChange?: (pageSize: PageSize) => void;
  onPageSizePreferenceChange?: (pageSize: PageSize) => void;
}) => {
  // Priority: URL > localStorage preference > default
  const effectivePageSize =
    urlPageSize && PAGE_SIZE_OPTIONS.some((opt) => opt.value === urlPageSize)
      ? urlPageSize
      : localStoragePageSize || INITIAL_PAGE_SIZE;

  const [state, dispatch] = React.useReducer(paginationReducer, {
    currentPage: 1,
    pageSize: effectivePageSize,
    paginationQueryVariables: {
      first: Number(effectivePageSize),
      last: undefined,
      before: undefined,
      after: undefined,
    },
  });

  const { endCursor, startCursor } = readFragment<typeof pageInfoFragment>(pageInfo) || {};

  const handleGoToFirstPage = () => {
    dispatch({ type: "GO_TO_FIRST_PAGE" });
  };
  const handleGoToNextPage = () => {
    dispatch({
      type: "GO_TO_NEXT_PAGE",
      endCursor: endCursor,
    });
  };

  const handleGoToPreviousPage = () => {
    dispatch({
      type: "GO_TO_PREVIOUS_PAGE",
      startCursor: startCursor,
    });
  };

  const handleGoToLastPage = () => {
    dispatch({ type: "GO_TO_LAST_PAGE", totalCount });
  };

  const previousTotalCount = usePrevious(totalCount);

  const totalPages = getTotalPages({
    totalCount: loading ? (previousTotalCount ?? totalCount) : totalCount,
    pageSize: Number(state.pageSize),
  });

  const isPreviousPageDisabled = state.currentPage <= 1 || loading;
  const isNextPageDisabled =
    state.currentPage >= getTotalPages({ totalCount, pageSize: Number(state.pageSize) }) || loading;

  const handleSetPageSize = (pageSize: PageSize) => {
    dispatch({ type: "SET_PAGE_SIZE", pageSize });
    // Update URL if callback provided
    if (onUrlPageSizeChange) {
      onUrlPageSizeChange(pageSize);
    }
    // Update localStorage preference if callback provided
    if (onPageSizePreferenceChange) {
      onPageSizePreferenceChange(pageSize);
    }
  };

  const handleResetPagination = () => dispatch({ type: "RESET_PAGINATION" });

  return {
    type: "server" as const,
    paginationQueryVariables: state.paginationQueryVariables,
    paginationInfo: {
      pageSize: state.pageSize,
      totalCount,
      totalPages,
      currentPage: state.currentPage,
    },
    controls: {
      handleResetPagination,
      handleGoToFirstPage,
      handleGoToLastPage,
      handleGoToNextPage,
      handleGoToPreviousPage,
      handleSetPageSize,
    },
    flags: {
      isNextPageDisabled,
      isPreviousPageDisabled,
    },
  };
};

export const getTotalPages = ({ totalCount, pageSize }: { totalCount: number; pageSize: number }): number => {
  if (pageSize === 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(totalCount / pageSize) || 1);
};
